import { useCallback, useEffect, useState } from 'react';
import { ModerationNav } from '../components/ModerationNav';
import {
  createPairing,
  fetchPool,
  suggestPair,
  type MeResponse,
  type PoolMember,
} from '../lib/api';

/** Moderator matching tool: view the pool and pair members 1:1. */
export function Moderation({ me }: { me: MeResponse }) {
  const membership = me.memberships[0];
  const communityId = membership?.communityId ?? '';
  const [pool, setPool] = useState<PoolMember[] | null>(null);
  const [memberA, setMemberA] = useState('');
  const [memberB, setMemberB] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!communityId) return;
    fetchPool(communityId)
      .then(setPool)
      .catch(() => setError('Could not load the matching pool.'));
  }, [communityId]);

  useEffect(load, [load]);

  const unmatched = (pool ?? []).filter((m) => !m.hasActiveMatch);

  async function onSuggest() {
    setError(null);
    setMessage(null);
    const pair = await suggestPair(communityId).catch(() => null);
    if (pair) {
      setMemberA(pair.a.membershipId);
      setMemberB(pair.b.membershipId);
    } else {
      setMessage('No unmatched pair is available to suggest.');
    }
  }

  async function onPair() {
    setError(null);
    setMessage(null);
    if (!memberA || !memberB) {
      setError('Choose two members to pair.');
      return;
    }
    setBusy(true);
    const result = await createPairing(communityId, memberA, memberB);
    setBusy(false);
    if (result.ok) {
      setMessage('Pairing created.');
      setMemberA('');
      setMemberB('');
      load();
    } else {
      setError(result.error);
    }
  }

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <h1>Matching</h1>
        <p>{membership?.communityName ?? 'Community'} — pair members into 1:1 conversations.</p>

        <section>
          <h2>Pool</h2>
          {!pool && <p>Loading…</p>}
          {pool && pool.length === 0 && <p className="muted">No members yet.</p>}
          {pool && pool.length > 0 && (
            <ul>
              {pool.map((m) => (
                <li key={m.membershipId}>
                  {m.nickname} — {m.hasActiveMatch ? 'matched' : 'available'}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2>Create a pairing</h2>
          <div className="stack">
            <label>
              Member A
              <select value={memberA} onChange={(event) => setMemberA(event.target.value)}>
                <option value="">— choose —</option>
                {unmatched.map((m) => (
                  <option key={m.membershipId} value={m.membershipId}>
                    {m.nickname}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Member B
              <select value={memberB} onChange={(event) => setMemberB(event.target.value)}>
                <option value="">— choose —</option>
                {unmatched.map((m) => (
                  <option key={m.membershipId} value={m.membershipId}>
                    {m.nickname}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="secondary" onClick={onSuggest}>
              Suggest a random pair
            </button>
            <button type="button" disabled={busy} onClick={onPair}>
              {busy ? 'Pairing…' : 'Create pairing'}
            </button>
            {message && <p className="muted">{message}</p>}
            {error && <p className="error">{error}</p>}
          </div>
        </section>
      </div>
    </main>
  );
}
